import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, BarChart3, Lock, Sparkles, Globe, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import ShortenForm from "@/components/ShortenForm";
import QRDialog from "@/components/QRDialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import AuthDialog from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";

function CopyBtn({ value }) {
  const [flash, setFlash] = useState(false);
  return (
    <button
      data-testid="copy-short-link"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setFlash(true); setTimeout(() => setFlash(false), 500);
        toast.success("Copied");
      }}
      className={`px-3 py-1.5 text-xs font-mono border border-border hover:bg-foreground hover:text-background transition-colors ${flash ? "copy-flash" : ""}`}
    >
      Copy
    </button>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [lastResult, setLastResult] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pt-4 md:pt-8 pb-16 md:pb-24 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="eyebrow text-muted-foreground mb-6" data-testid="hero-eyebrow">
              Splink.pro · The SocialPulse network shortener
            </div>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mb-8" data-testid="hero-title">
              Short links.
              <br />
              <span className="text-accent">Sharp</span> QR codes.
              <br />
              <span className="text-muted-foreground">One identity.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-10">
              Shorten, brand, track. Use the same account you already have on SocialPulse, FoodPulse, IndianFamilyTree & NaturePulse.
            </p>

            <div className="max-w-3xl">
              <ShortenForm
                isDemo={!user}
                onCreated={(res) => { setLastResult(res); }}
              />
              {!user && (
                <p className="mt-3 text-xs text-muted-foreground font-mono" data-testid="demo-disclaimer">
                  &gt; Demo links auto-expire and are deleted after 7 days. <button className="link-underline text-foreground ml-1" onClick={() => setAuthOpen(true)}>Sign in</button> for permanent links.
                </p>
              )}
            </div>

            {lastResult && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="mt-6 border border-foreground p-4 md:p-5 max-w-3xl flex items-center gap-3 md:gap-4 flex-wrap"
                data-testid="last-result"
              >
                <div className="flex-1 min-w-0">
                  <div className="eyebrow text-muted-foreground mb-1">Your short link</div>
                  <a href={lastResult.short_url} target="_blank" rel="noopener noreferrer" data-testid="last-result-url" className="font-mono text-sm md:text-base font-medium break-all hover:underline">
                    {lastResult.short_url}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <CopyBtn value={lastResult.short_url} />
                  <button
                    data-testid="show-qr-btn"
                    onClick={() => setQrOpen(true)}
                    className="px-3 py-1.5 text-xs font-medium border border-foreground bg-foreground text-background hover:opacity-90 transition-opacity"
                  >
                    QR code
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right column: mini analytics preview */}
          <div className="lg:col-span-4 hidden lg:block">
            <div className="border border-border h-full p-6 flex flex-col">
              <div className="eyebrow text-muted-foreground mb-4">Analytics preview</div>
              <div className="flex-1 flex items-end gap-1">
                {[42, 68, 24, 89, 51, 76, 33, 95, 61, 44, 82, 58, 71, 39, 87].map((h, i) => (
                  <div key={i} className="flex-1 bg-foreground" style={{ height: `${h}%`, minHeight: 4 }} />
                ))}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-border">
                <div><div className="text-xs text-muted-foreground">Clicks</div><div className="font-mono text-lg">12,483</div></div>
                <div><div className="text-xs text-muted-foreground">Links</div><div className="font-mono text-lg">247</div></div>
                <div><div className="text-xs text-muted-foreground">Countries</div><div className="font-mono text-lg">38</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-24">
        <div className="border-t border-border pt-16">
          <div className="eyebrow text-muted-foreground mb-6">What's inside</div>
          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 md:gap-6">
            <FeatureCard cls="md:col-span-8 lg:col-span-7" icon={<Zap />} title="Fast, brandable slugs" body="Custom aliases, password protection, expiration windows and dynamic destinations." />
            <FeatureCard cls="md:col-span-4 lg:col-span-5" icon={<BarChart3 />} title="Analytics that don't lie" body="Clicks, geography, device, referrer — server-side, no client fingerprinting." />
            <FeatureCard cls="md:col-span-4 lg:col-span-4" icon={<Layers />} title="QR codes, PNG + SVG" body="Print-ready SVG downloads. Dynamic QR on Pro+ plans." />
            <FeatureCard cls="md:col-span-4 lg:col-span-4" icon={<Globe />} title="Shared identity" body="One login across the whole SocialPulse network. Auto-provisioned on first sign-in." />
            <FeatureCard cls="md:col-span-8 lg:col-span-4" icon={<Lock />} title="Multi-tenant API" body="Machine-to-machine tenant keys. SocialPulse & child sites plug in via /api/shorten." />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-32">
        <div className="border border-foreground p-8 md:p-16 grid md:grid-cols-3 gap-8 items-end">
          <div className="md:col-span-2">
            <div className="eyebrow text-muted-foreground mb-4">Start free</div>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter mb-4">25 permanent links. No card. No trial timer.</h2>
            <p className="text-muted-foreground max-w-xl">Upgrade when you outgrow it. Or top up with a one-time lifetime pack.</p>
          </div>
          <div className="flex md:justify-end gap-3">
            <Link to="/pricing"><Button data-testid="cta-see-pricing" variant="outline" className="rounded-none border-foreground h-12 px-6">See pricing</Button></Link>
            {!user && <Button data-testid="cta-signin" onClick={() => setAuthOpen(true)} className="rounded-none bg-foreground text-background hover:bg-foreground/90 h-12 px-6">Sign in <ArrowUpRight className="w-4 h-4 ml-2" /></Button>}
            {user && <Link to="/dashboard"><Button data-testid="cta-dashboard" className="rounded-none bg-foreground text-background hover:bg-foreground/90 h-12 px-6">Dashboard <ArrowUpRight className="w-4 h-4 ml-2" /></Button></Link>}
          </div>
        </div>
      </section>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      {lastResult && <QRDialog open={qrOpen} onOpenChange={setQrOpen} shortUrl={lastResult.short_url} linkId={lastResult.link.id} />}
    </div>
  );
}

function FeatureCard({ cls, icon, title, body }) {
  return (
    <div className={`${cls} border border-border p-6 md:p-8 hover:border-foreground transition-colors group`}>
      <div className="mb-4 opacity-60 group-hover:opacity-100 transition-opacity">{React.cloneElement(icon, { className: "w-5 h-5" })}</div>
      <div className="font-heading text-xl md:text-2xl font-light tracking-tight mb-2">{title}</div>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
